import React, { useEffect, useState, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, FlatList, TouchableOpacity, ActivityIndicator, ScrollView, Dimensions, Share, Alert, Modal, Pressable, TextInput, KeyboardAvoidingView, Platform, Linking, SafeAreaView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { Video, ResizeMode } from 'expo-av';
import * as Clipboard from 'expo-clipboard';
import { useTranslation } from 'react-i18next';
import { getPostThread, like, unlike, repost, unrepost, createPost, deletePost, getDid } from '../services/bluesky';
import { extractPlainText } from '../utils/text';
import { savePost } from '../utils/saved';
import Hyperlink from '../components/Hyperlink';
import { Theme, useTheme, useThemedStyles } from '../theme';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface PostDetailProps {
  post: any;
  onBack: () => void;
  onSelectPost: (post: any) => void;
  onSelectUser: (did: string) => void;
  onShowUserList: (type: 'likes' | 'reposts' | 'quotes', uri: string) => void;
  onSearchHashtag?: (hashtag: string) => void;
  onSearchHandle?: (handle: string) => void;
}

const PostDetail: React.FC<PostDetailProps> = ({ 
  post: initialPost, 
  onBack, 
  onSelectPost, 
  onSelectUser,
  onShowUserList,
  onSearchHashtag,
  onSearchHandle
}) => {
  const insets = useSafeAreaInsets();
  const [thread, setThread] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [mainPost, setMainPost] = useState(initialPost);
  const [postMenuVisible, setPostMenuVisible] = useState(false);
  const [replyModalVisible, setReplyModalVisible] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isQuote, setIsQuote] = useState(false);
  const [replyTarget, setReplyTarget] = useState<any>(null);
  const [selectedMedia, setSelectedMedia] = useState<{ url: string, type: 'image' | 'video' } | null>(null);
  const [mediaModalVisible, setMediaModalVisible] = useState(false);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useThemedStyles(createStyles);

  const fetchThread = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPostThread(mainPost.uri);
      setThread(data);
    } catch (e) {
      console.error('Failed to fetch thread', e);
    } finally {
      setLoading(false);
    }
  }, [mainPost.uri]);

  useEffect(() => {
    fetchThread();
  }, [fetchThread]);

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  // Cache for fetched link previews to avoid repeated fetches
  const previewCache = useRef<Record<string, any>>({});

  const fetchLinkPreview = async (url: string) => {
    if (!url) return null;
    if (previewCache.current[url]) return previewCache.current[url];
    try {
      const res = await fetch(url, { method: 'GET' });
      const text = await res.text();
      // crude OG meta extraction
      const ogRegex = /<meta[^>]+property=["']og:([^"']+)["'][^>]*content=["']([^"']+)["'][^>]*>/gi;
      const nameRegex = /<meta[^>]+name=["']twitter:([^"']+)["'][^>]*content=["']([^"']+)["'][^>]*>/gi;
      let m: RegExpExecArray | null;
      const out: any = {};
      while ((m = ogRegex.exec(text)) !== null) {
        out[m[1]] = m[2];
      }
      while ((m = nameRegex.exec(text)) !== null) {
        out[m[1]] = m[2];
      }
      // fallback: find <title>
      if (!out.title) {
        const t = /<title[^>]*>([^<]+)<\/title>/i.exec(text);
        if (t) out.title = t[1];
      }
      previewCache.current[url] = out;
      return out;
    } catch (e) {
      previewCache.current[url] = null;
      return null;
    }
  };

  const findExternalFromPost = (postObj: any) => {
    const embed = postObj.embed || postObj.record?.embed || postObj.record?.value?.embed || postObj.record?.record?.embed || postObj.value?.embed || postObj.value?.record?.embed;
    const ext = embed?.external || embed?.media?.external || embed?.record?.external || postObj.external || postObj.record?.external || postObj.record?.value?.external;
    if (ext && (ext.uri || ext.url)) {
      return {
        uri: ext.uri || ext.url,
        title: ext.title || ext.name,
        description: ext.description || ext.desc,
        thumb: ext.thumb || ext.image || ext.thumbnail,
      };
    }

    // fallback: try to extract first URL from plain text
    const text = extractPlainText(postObj) || '';
    const urlMatch = text.match(/https?:\/\/[\w\-._~:\/?#\[\]@!$&'()*+,;=%]+/i);
    if (urlMatch) return { uri: urlMatch[0] };
    return null;
  };

  const ExternalPreview: React.FC<{ post: any }> = ({ post }) => {
    const ext = findExternalFromPost(post);
    const [preview, setPreview] = useState<any>(null);

    useEffect(() => {
      if (!ext) return;
      let mounted = true;
      (async () => {
        if (ext.thumb || ext.title || ext.description) {
          if (mounted) setPreview({ title: ext.title, description: ext.description, image: ext.thumb });
          return;
        }
        const p = await fetchLinkPreview(ext.uri);
        if (mounted) setPreview(p);
      })();
      return () => { mounted = false; };
    }, [ext?.uri]);

    if (!ext) return null;

    return (
      <TouchableOpacity style={styles.externalContainer} onPress={() => Linking.openURL(ext.uri)} activeOpacity={0.8}>
        {preview?.image ? (
          <Image source={{ uri: preview.image }} style={styles.externalThumb} contentFit="cover" />
        ) : null}
        <View style={styles.externalContent}>
          <Text style={styles.externalTitle} numberOfLines={2}>{preview?.title || ext.uri}</Text>
          {preview?.description ? <Text style={styles.externalDescription} numberOfLines={2}>{preview.description}</Text> : null}
          <Text style={styles.externalUri} numberOfLines={1}>{(ext.uri || '').replace(/^https?:\/\//, '')}</Text>
        </View>
      </TouchableOpacity>
    );
  };

  const handleLike = async (post: any, isMain: boolean = false) => {
    const isLiked = !!post.viewer?.like;
    try {
      if (isLiked) {
        await unlike(post.viewer.like);
        const updatedPost = {
          ...post,
          likeCount: (post.likeCount || 0) - 1,
          viewer: { ...post.viewer, like: undefined }
        };
        if (isMain) setMainPost(updatedPost);
        // Update in thread if needed (complex for nested)
        fetchThread();
      } else {
        const res = await like(post.uri, post.cid);
        const updatedPost = {
          ...post,
          likeCount: (post.likeCount || 0) + 1,
          viewer: { ...post.viewer, like: res.uri }
        };
        if (isMain) setMainPost(updatedPost);
        fetchThread();
      }
    } catch (e) {
      console.error('Like failed', e);
    }
  };

  const handleRepost = async (post: any, isMain: boolean = false) => {
    if (post.viewer?.repost) {
      try {
        await unrepost(post.viewer.repost);
        fetchThread();
      } catch (e) {
        console.error('Unrepost failed', e);
      }
    } else {
      setReplyTarget(post);
      setPostMenuVisible(true);
    }
  };

  const executeRepost = async () => {
    if (!replyTarget) return;
    setPostMenuVisible(false);
    try {
      await repost(replyTarget.uri, replyTarget.cid);
      fetchThread();
    } catch (e) {
      console.error('Repost failed', e);
    }
  };

  const executeQuote = () => {
    setPostMenuVisible(false);
    setIsQuote(true);
    setReplyText('');
    setReplyModalVisible(true);
  };

  const executeSave = async () => {
    if (!replyTarget) return;
    setPostMenuVisible(false);
    await savePost(replyTarget);
    Alert.alert(t('messageBubble.saved'));
  };

  const executeCopyLink = async () => {
    if (!replyTarget) return;
    setPostMenuVisible(false);
    await handleCopyLink(replyTarget);
  };

  const executeOpenInBrowser = () => {
    if (!replyTarget) return;
    setPostMenuVisible(false);
    const url = `https://bsky.app/profile/${replyTarget.author.handle}/post/${replyTarget.uri.split('/').pop()}`;
    Linking.openURL(url);
  };

  const handlePostMenu = (post: any) => {
    setReplyTarget(post);
    setPostMenuVisible(true);
  };

  const handleShare = async (post: any) => {
    try {
      const url = `https://bsky.app/profile/${post.author.handle}/post/${post.uri.split('/').pop()}`;
      await Share.share({
        message: `${extractPlainText(post)}\n\n${url}`,
        url: url,
      });
    } catch (error) {
      console.error('Share failed', error);
    }
  };

  const handleSave = async (post: any) => {
    try {
      // Ensure we pass images for local saving
      const images = post.embed?.images || post.embed?.media?.images || post.images;
      await savePost({ ...post, text: extractPlainText(post), images });
      Alert.alert('保存しました');
    } catch (e) {
      console.error('Save failed', e);
      Alert.alert('エラー', '保存に失敗しました');
    }
  };

  const handleCopyLink = async (post: any) => {
    const url = `https://bsky.app/profile/${post.author.handle}/post/${post.uri.split('/').pop()}`;
    await Clipboard.setStringAsync(url);
    Alert.alert(t('postDetail.copyDone'), t('postDetail.copyDesc'));
  };

  const handleDelete = (post: any) => {
    Alert.alert(
      t('common.delete'),
      t('messageBubble.deleteDesc'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        { 
          text: t('common.delete'), 
          style: 'destructive',
          onPress: async () => {
            try {
              await deletePost(post.uri);
              if (post.uri === mainPost.uri) {
                onBack();
              } else {
                fetchThread();
              }
            } catch (e: any) {
              Alert.alert(t('common.error'), e.message || t('messageBubble.deleteFailed'));
            }
          }
        }
      ]
    );
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !replyTarget) return;
    setIsSending(true);
    try {
      if (isQuote) {
        await createPost({
          text: replyText,
          quote: {
            uri: replyTarget.uri,
            cid: replyTarget.cid
          }
        });
      } else {
        const root = replyTarget.record?.reply?.root || { uri: replyTarget.uri, cid: replyTarget.cid };
        await createPost({
          text: replyText,
          reply: {
            root: root,
            parent: { uri: replyTarget.uri, cid: replyTarget.cid }
          }
        });
      }
      setReplyText('');
      setReplyModalVisible(false);
      fetchThread();
    } catch (error) {
      console.error('Failed to send:', error);
      Alert.alert('エラー', isQuote ? '引用リポストの送信に失敗しました。' : '返信の送信に失敗しました。');
    } finally {
      setIsSending(false);
    }
  };

  const openMedia = (url: string, type: 'image' | 'video') => {
    setSelectedMedia({ url, type });
    setMediaModalVisible(true);
  };

  const renderPostMedia = (post: any) => {
    // If we have local images (from SavedPosts), use them
    if (post.localImages && post.localImages.length > 0) {
      return (
        <View style={styles.mediaContainer}>
          <View style={styles.imageGrid}>
            {post.localImages.map((uri: string, i: number) => (
              <TouchableOpacity
                key={i}
                onPress={() => openMedia(uri, 'image')}
                style={{ width: post.localImages.length === 1 ? '100%' : '49%', aspectRatio: 1, marginBottom: 5 }}
              >
                <Image
                  source={{ uri }}
                  style={styles.gridImage}
                  contentFit="cover"
                />
              </TouchableOpacity>
            ))}
          </View>
        </View>
      );
    }

    // Normalize embed locations (thread.post, viewRecord, record.value, etc.)
    const embed = post.embed || post.record?.embed || post.record?.value?.embed || post.record?.record?.embed || post.value?.embed || post.value?.record?.embed;

    const imagesRaw = embed?.images || embed?.media?.images || embed?.record?.images || embed?.record?.value?.images;
    const videoRaw = embed?.playlist || embed?.media?.playlist || embed?.record?.playlist || embed?.record?.value?.playlist || embed?.video || embed?.media?.video;

    const normalizeImageUrl = (img: any) => {
      if (!img) return null;
      return img.fullsize || img.full || img.image || img.thumb || img.uri || (img.image && (img.image.fullsize || img.image.thumb)) || null;
    };

    const images = Array.isArray(imagesRaw) ? imagesRaw.map((i: any) => ({ uri: normalizeImageUrl(i) })).filter((x: any) => x.uri) : [];
    const video = typeof videoRaw === 'string' ? videoRaw : (videoRaw && (videoRaw.playlist || videoRaw.uri || videoRaw.url || videoRaw.src));

    if ((!images || images.length === 0) && !video) return null;

    return (
      <View style={styles.mediaContainer}>
        {images && images.length > 0 && (
          <View style={styles.imageGrid}>
            {images.map((img: any, i: number) => (
              <TouchableOpacity
                key={i}
                onPress={() => openMedia(img.uri, 'image')}
                style={{ width: images.length === 1 ? '100%' : '49%', aspectRatio: 1, marginBottom: 5 }}
              >
                <Image
                  source={{ uri: img.uri }}
                  style={styles.gridImage}
                  contentFit="cover"
                />
              </TouchableOpacity>
            ))}
          </View>
        )}

        {video && (
          <TouchableOpacity onPress={() => openMedia(video, 'video')}>
            <View style={styles.videoThumbnailContainer}>
              <Video
                source={{ uri: video }}
                style={styles.videoThumbnail}
                resizeMode={ResizeMode.COVER}
                shouldPlay={false}
              />
              <View style={styles.playIconOverlay}>
                <Ionicons name="play" size={40} color="white" />
              </View>
            </View>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const renderQuote = (embed: any) => {
    const quote = embed?.record || embed?.record?.record;
    if (!quote || (quote.$type !== 'app.bsky.embed.record#viewRecord' && quote.$type !== 'app.bsky.feed.defs#postView')) return null;

    return (
      <TouchableOpacity 
        style={styles.quoteBox}
        onPress={() => onSelectPost(quote)}
      >
        <View style={{ flexDirection: 'row' }}>
          <View style={{ flex: 1 }}>
            <View style={styles.quoteHeader}>
              <Image source={{ uri: quote.author.avatar }} style={styles.quoteAvatar} />
              <Text style={styles.quoteAuthor} numberOfLines={1}>
                {quote.author.displayName || quote.author.handle}
              </Text>
            </View>
            <Text style={styles.quoteText} numberOfLines={3}>
              {extractPlainText(quote) || ''}
            </Text>
          </View>
          
          {/* Quoted Post Image Preview */}
          {(quote.embed?.images && quote.embed.images.length > 0) && (
            <Image 
              source={{ uri: quote.embed.images[0].thumb }} 
              style={styles.quoteImagePreview}
            />
          )}
          {quote.embed?.$type === 'app.bsky.embed.recordWithMedia#view' && 
           quote.embed.media?.images && quote.embed.media.images.length > 0 && (
            <Image 
              source={{ uri: quote.embed.media.images[0].thumb }} 
              style={styles.quoteImagePreview}
            />
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const renderThreadItem = (post: any, isMain: boolean = false, depth: number = 0, hasNext: boolean = false) => {
    if (!post) return null;
    const myDid = getDid();
    const isOwnPost = post.author.did === myDid;

    return (
      <View style={[styles.threadItemContainer, { marginLeft: depth > 0 ? 12 : 0 }]}>
        {depth > 0 && <View style={styles.threadLine} />}
        <View 
          style={[styles.postCard, isMain && styles.mainPostCard]}
        >
          <View style={styles.postHeader}>
            <TouchableOpacity onPress={() => onSelectUser(post.author.did)}>
              <Image source={{ uri: post.author.avatar }} style={isMain ? styles.mainAvatar : styles.replyAvatar} />
            </TouchableOpacity>
            <View style={styles.authorInfo}>
              <TouchableOpacity style={styles.authorNameRow} onPress={() => onSelectUser(post.author.did)}>
                <Text style={styles.authorName} numberOfLines={1}>
                  {post.author.displayName || post.author.handle}
                </Text>
                {!isMain && <Text style={styles.postTime}> · {formatTime(post.indexedAt).split(' ')[1]}</Text>}
              </TouchableOpacity>
              <Text style={styles.handle}>@{post.author.handle}</Text>
            </View>
            <TouchableOpacity onPress={() => {
              setReplyTarget(post);
              // Show more options menu
              Alert.alert(
                t('postDetail.options'),
                '',
                [
                  { text: t('messageBubble.save'), onPress: () => handleSave(post) },
                  { text: t('postDetail.copyLink'), onPress: () => handleCopyLink(post) },
                  { text: t('postDetail.openBrowser'), onPress: () => Linking.openURL(`https://bsky.app/profile/${post.author.handle}/post/${post.uri.split('/').pop()}`) },
                  isOwnPost ? { text: t('common.delete'), style: 'destructive', onPress: () => handleDelete(post) } : { text: t('talkRoom.report'), style: 'destructive' },
                  { text: t('common.cancel'), style: 'cancel' }
                ]
              );
            }}>
              <Ionicons name="ellipsis-horizontal" size={20} color={theme.colors.textMuted} />
            </TouchableOpacity>
          </View>

          <Hyperlink 
            linkDefault={true} 
            linkStyle={{ color: theme.colors.primary, textDecorationLine: 'underline' }} 
            style={isMain ? styles.mainText : styles.replyText}
            onHashtagPress={onSearchHashtag}
            onHandlePress={onSearchHandle}
            selectable={true}
          >
            {extractPlainText(post)}
          </Hyperlink>
          
          <ExternalPreview post={post} />
          {renderPostMedia(post)}
          {renderQuote(post.embed)}

          {isMain && (
            <>
              <Text style={styles.timestamp}>{formatTime(post.indexedAt)}</Text>
              <View style={styles.statsRow}>
                <TouchableOpacity onPress={() => onShowUserList('reposts', post.uri)}>
                  <Text style={styles.statText}><Text style={styles.statNumber}>{post.repostCount || 0}</Text> リポスト</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => onShowUserList('quotes', post.uri)} style={{ marginLeft: 15 }}>
                  <Text style={styles.statText}><Text style={styles.statNumber}>{post.quoteCount || 0}</Text> 引用</Text>
                </TouchableOpacity>
                <TouchableOpacity onPress={() => onShowUserList('likes', post.uri)} style={{ marginLeft: 15 }}>
                  <Text style={styles.statText}><Text style={styles.statNumber}>{post.likeCount || 0}</Text> いいね</Text>
                </TouchableOpacity>
              </View>
            </>
          )}

          <View style={styles.actionsRow}>
            <TouchableOpacity style={styles.actionButton} onPress={() => {
              setReplyTarget(post);
              setIsQuote(false);
              setReplyModalVisible(true);
            }}>
              <Ionicons name="chatbubble-outline" size={20} color={theme.colors.textMuted} />
              {!isMain && post.replyCount > 0 && <Text style={styles.actionCount}>{post.replyCount}</Text>}
            </TouchableOpacity>
            <TouchableOpacity style={styles.actionButton} onPress={() => handleRepost(post, isMain)}>
              <Ionicons name="repeat" size={22} color={post.viewer?.repost ? theme.colors.primary : theme.colors.textMuted} />
              {!isMain && post.repostCount > 0 && <Text style={[styles.actionCount, post.viewer?.repost && { color: theme.colors.primary }]}>{post.repostCount}</Text>}
            </TouchableOpacity>
            <TouchableOpacity style={styles.actionButton} onPress={() => handleLike(post, isMain)}>
              <Ionicons 
                name={post.viewer?.like ? "heart" : "heart-outline"} 
                size={22} 
                color={post.viewer?.like ? "#FF4B4B" : theme.colors.textMuted} 
              />
              {!isMain && post.likeCount > 0 && <Text style={[styles.actionCount, post.viewer?.like && { color: '#FF4B4B' }]}>{post.likeCount}</Text>}
            </TouchableOpacity>
            <TouchableOpacity style={styles.actionButton} onPress={() => handleSave(post)}>
              <Ionicons name="bookmark-outline" size={20} color={theme.colors.textMuted} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.actionButton} onPress={() => handleShare(post)}>
              <Ionicons name="share-social-outline" size={20} color={theme.colors.textMuted} />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const renderParentChain = (node: any) => {
    if (!node || !node.parent) return null;
    const parents = [];
    let current = node.parent;
    while (current) {
      if (current.post) parents.unshift(current.post);
      current = current.parent;
    }
    return parents.map((p, i) => (
      <View key={p.uri || p.cid || `parent-${i}`}>
        {renderThreadItem(p, false, 0, true)}
        <View style={styles.parentConnector} />
      </View>
    ));
  };

  const renderReplies = (replies: any[], depth: number = 0) => {
    if (!replies || replies.length === 0) return null;
    return replies.map((reply, i) => (
      <View key={reply.post?.uri || reply.post?.cid || `r-${depth}-${i}`}>
        {renderThreadItem(reply.post, false, depth + 1)}
        {renderReplies(reply.replies, depth + 1)}
      </View>
    ));
  };

  return (
    <View style={[styles.container, { paddingTop: insets.top, paddingLeft: insets.left, paddingRight: insets.right }]}>
      <View style={styles.header}>
        <TouchableOpacity onPress={onBack} style={styles.backButton}>
          <Ionicons name="chevron-back" size={28} color={theme.colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>スレッド</Text>
        <TouchableOpacity onPress={fetchThread} style={styles.refreshButton}>
          <Ionicons name="refresh" size={24} color={theme.colors.text} />
        </TouchableOpacity>
      </View>

      {loading && !thread ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
        </View>
      ) : (
        <ScrollView style={styles.content} contentContainerStyle={{ paddingBottom: insets.bottom + 20 }}>
          {renderParentChain(thread)}
          {renderThreadItem(thread?.post || mainPost, true)}
          <View style={styles.repliesDivider} />
          {renderReplies(thread?.replies)}
        </ScrollView>
      )}

      {/* Post Menu Modal */}
      <Modal
        visible={postMenuVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setPostMenuVisible(false)}
      >
        <Pressable 
          style={styles.modalOverlay} 
          onPress={() => setPostMenuVisible(false)}
        >
          <View style={styles.menuContainer}>
            <TouchableOpacity style={styles.menuItem} onPress={executeRepost}>
              <Ionicons name="repeat" size={24} color={theme.colors.text} />
              <Text style={styles.menuText}>リポスト</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.menuItem} onPress={executeQuote}>
              <Ionicons name="create-outline" size={24} color={theme.colors.text} />
              <Text style={styles.menuText}>引用</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.menuItem} onPress={executeSave}>
              <Ionicons name="bookmark-outline" size={24} color={theme.colors.text} />
              <Text style={styles.menuText}>保存</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.menuItem} onPress={executeCopyLink}>
              <Ionicons name="copy-outline" size={24} color={theme.colors.text} />
              <Text style={styles.menuText}>リンクをコピー</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.menuItem} onPress={executeOpenInBrowser}>
              <Ionicons name="globe-outline" size={24} color={theme.colors.text} />
              <Text style={styles.menuText}>ブラウザで開く</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.menuItem, styles.cancelItem]} 
              onPress={() => setPostMenuVisible(false)}
            >
              <Text style={styles.cancelText}>キャンセル</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Modal>

      {/* Reply Modal */}
      <Modal
        visible={replyModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setReplyModalVisible(false)}
      >
        <KeyboardAvoidingView 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.replyModalContainer}
        >
          <View style={styles.replyModalContent}>
            <View style={styles.replyModalHeader}>
              <TouchableOpacity onPress={() => setReplyModalVisible(false)}>
                <Text style={styles.replyCancelText}>キャンセル</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.replySendButton, !replyText.trim() && styles.replySendButtonDisabled]}
                onPress={handleSendReply}
                disabled={!replyText.trim() || isSending}
              >
                {isSending ? (
                  <ActivityIndicator size="small" color={theme.colors.primaryText} />
                ) : (
                  <Text style={styles.replySendText}>{isQuote ? '投稿' : '返信'}</Text>
                )}
              </TouchableOpacity>
            </View>
            
            {replyTarget && (
              <View style={styles.replyTargetPreview}>
                <Image source={{ uri: replyTarget.author.avatar }} style={styles.replyTargetAvatar} />
                <View style={styles.replyTargetInfo}>
                  <Text style={styles.replyTargetAuthor}>
                    {isQuote ? `@${replyTarget.author.handle} を引用` : `@${replyTarget.author.handle} に返信`}
                  </Text>
                  <Text style={styles.replyTargetText} numberOfLines={2}>{extractPlainText(replyTarget)}</Text>
                </View>
              </View>
            )}

            <TextInput
              style={styles.replyInput}
              placeholder={isQuote ? "コメントを追加" : "返信をツイート"}
              placeholderTextColor={theme.colors.textMuted}
              multiline
              autoFocus
              value={replyText}
              onChangeText={setReplyText}
              maxLength={300}
            />
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Media Modal */}
      <Modal
        visible={mediaModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setMediaModalVisible(false)}
      >
        <SafeAreaView style={styles.mediaModalContainer}>
          <TouchableOpacity 
            style={styles.mediaModalClose} 
            onPress={() => setMediaModalVisible(false)}
          >
            <Ionicons name="close" size={30} color="white" />
          </TouchableOpacity>
          
          <View style={styles.mediaModalContent}>
            {selectedMedia?.type === 'image' ? (
              <Image 
                source={{ uri: selectedMedia.url }} 
                style={styles.fullImage} 
                contentFit="contain"
              />
            ) : selectedMedia?.type === 'video' ? (
              <Video
                source={{ uri: selectedMedia.url }}
                style={styles.fullVideo}
                useNativeControls
                resizeMode={ResizeMode.CONTAIN}
                shouldPlay
              />
            ) : null}
          </View>
        </SafeAreaView>
      </Modal>
    </View>
  );
};

const createStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    height: 60,
    backgroundColor: theme.colors.card,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 10,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  headerTitle: {
    color: theme.colors.text,
    fontSize: 18,
    fontWeight: 'bold',
  },
  backButton: {
    padding: 5,
  },
  refreshButton: {
    padding: 5,
  },
  content: {
    flex: 1,
  },
  threadItemContainer: {
    position: 'relative',
  },
  threadLine: {
    position: 'absolute',
    left: -10,
    top: 0,
    bottom: 0,
    width: 2,
    backgroundColor: theme.colors.border,
  },
  parentConnector: {
    width: 2,
    height: 10,
    backgroundColor: theme.colors.border,
    marginLeft: 30,
  },
  postCard: {
    backgroundColor: theme.colors.card,
    padding: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  mainPostCard: {
    borderBottomWidth: 0,
  },
  postHeader: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  authorInfo: {
    flex: 1,
    marginLeft: 10,
  },
  authorNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  authorName: {
    fontWeight: 'bold',
    fontSize: 16,
    color: theme.colors.text,
  },
  postTime: {
    color: theme.colors.textMuted,
    fontSize: 14,
  },
  handle: {
    color: theme.colors.textMuted,
    fontSize: 14,
  },
  mainAvatar: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: theme.colors.cardSecondary,
  },
  replyAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.cardSecondary,
  },
  mainText: {
    fontSize: 18,
    lineHeight: 24,
    color: theme.colors.text,
    marginBottom: 12,
  },
  replyText: {
    fontSize: 15,
    lineHeight: 20,
    color: theme.colors.text,
    marginBottom: 8,
  },
  imageGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  gridImage: {
    borderRadius: 12,
    width: '100%',
    height: '100%',
    backgroundColor: theme.colors.cardSecondary,
  },
  externalContainer: {
    backgroundColor: theme.colors.cardSecondary,
    borderRadius: 12,
    marginTop: 8,
    overflow: 'hidden',
    borderWidth: 0.5,
    borderColor: theme.colors.border,
  },
  externalThumb: {
    width: '100%',
    height: 150,
    backgroundColor: theme.colors.cardSecondary,
  },
  externalContent: {
    padding: 10,
  },
  externalTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: 4,
  },
  externalDescription: {
    fontSize: 12,
    color: theme.colors.textMuted,
    marginBottom: 4,
  },
  externalUri: {
    fontSize: 11,
    color: theme.colors.textMuted,
  },
  mediaContainer: {
    marginBottom: 10,
  },
  videoThumbnailContainer: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: theme.colors.cardSecondary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  videoThumbnail: {
    width: '100%',
    height: '100%',
  },
  playIconOverlay: {
    position: 'absolute',
    backgroundColor: 'rgba(0,0,0,0.4)',
    borderRadius: 40,
    padding: 10,
  },
  mediaModalContainer: {
    flex: 1,
    backgroundColor: 'black',
  },
  mediaModalClose: {
    position: 'absolute',
    top: 50,
    right: 20,
    zIndex: 10,
    padding: 10,
  },
  mediaModalContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullImage: {
    width: SCREEN_WIDTH,
    height: '100%',
  },
  fullVideo: {
    width: SCREEN_WIDTH,
    aspectRatio: 16 / 9,
  },
  quoteBox: {
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 12,
    padding: 10,
    marginBottom: 10,
    backgroundColor: theme.colors.cardSecondary,
    overflow: 'hidden',
  },
  quoteImagePreview: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginLeft: 10,
    backgroundColor: theme.colors.border,
  },
  quoteHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
  },
  quoteAvatar: {
    width: 20,
    height: 20,
    borderRadius: 10,
    marginRight: 8,
    backgroundColor: theme.colors.cardSecondary,
  },
  quoteAuthor: {
    fontWeight: 'bold',
    fontSize: 14,
    color: theme.colors.text,
  },
  quoteText: {
    fontSize: 14,
    color: theme.colors.text,
  },
  timestamp: {
    color: theme.colors.textMuted,
    fontSize: 14,
    marginBottom: 12,
    paddingTop: 12,
    borderTopWidth: 0.5,
    borderTopColor: theme.colors.border,
  },
  statsRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    borderTopWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: theme.colors.border,
    marginBottom: 12,
  },
  statText: {
    color: theme.colors.textMuted,
    marginRight: 20,
  },
  statNumber: {
    fontWeight: 'bold',
    color: theme.colors.text,
  },
  actionsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 4,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
  },
  actionCount: {
    marginLeft: 4,
    fontSize: 13,
    color: theme.colors.textMuted,
  },
  repliesDivider: {
    height: 8,
    backgroundColor: theme.colors.cardSecondary,
    borderTopWidth: 0.5,
    borderBottomWidth: 0.5,
    borderColor: theme.colors.border,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  menuContainer: {
    backgroundColor: theme.colors.card,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    paddingBottom: 40,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 15,
    borderBottomWidth: 0.5,
    borderBottomColor: theme.colors.border,
  },
  menuText: {
    fontSize: 18,
    marginLeft: 15,
    color: theme.colors.text,
  },
  cancelItem: {
    borderBottomWidth: 0,
    marginTop: 10,
    justifyContent: 'center',
  },
  cancelText: {
    fontSize: 18,
    color: theme.colors.danger,
    fontWeight: 'bold',
  },
  replyModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  replyModalContent: {
    backgroundColor: theme.colors.card,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    height: '90%',
    padding: 15,
  },
  replyModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  replyCancelText: {
    fontSize: 16,
    color: theme.colors.text,
  },
  replySendButton: {
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 20,
  },
  replySendButtonDisabled: {
    opacity: 0.6,
  },
  replySendText: {
    color: theme.colors.primaryText,
    fontWeight: 'bold',
    fontSize: 16,
  },
  replyTargetPreview: {
    flexDirection: 'row',
    marginBottom: 15,
    padding: 10,
    backgroundColor: theme.colors.cardSecondary,
    borderRadius: 12,
  },
  replyTargetAvatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: theme.colors.cardSecondary,
  },
  replyTargetInfo: {
    flex: 1,
    marginLeft: 10,
  },
  replyTargetAuthor: {
    fontSize: 12,
    color: theme.colors.textMuted,
    marginBottom: 2,
  },
  replyTargetText: {
    fontSize: 14,
    color: theme.colors.text,
  },
  replyInput: {
    flex: 1,
    fontSize: 18,
    textAlignVertical: 'top',
    color: theme.colors.text,
  },
});

export default PostDetail;
